import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../services/ApiService';

function VehicleSearched() {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState({
    searchId: '',
    vehicleNumber: '',
    found: '',
    date: ''
  });

  const [selectedSearch, setSelectedSearch] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpStep, setOtpStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const DOWNLOAD_PHONE = '9554494415';
  const itemsPerPage = 10;

  useEffect(() => {
    fetchSearchedVehicles();
  }, []);

  const fetchSearchedVehicles = async () => {
    setLoading(true);
    try {
      const result = await apiService.get('/vehicles/admin/searched-vehicles');
      setSearches(result.searchedVehicles || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch search history');
    } finally {
      setLoading(false);
    }
  };

  const filteredSearches = useMemo(() => {
    return searches.filter(s => {
      const matchesId = !searchParams.searchId || s.search_id?.toString().includes(searchParams.searchId);
      const matchesNumber = !searchParams.vehicleNumber || s.vehicle_number?.toLowerCase().includes(searchParams.vehicleNumber.toLowerCase());
      const matchesFound = searchParams.found === '' || s.found.toString() === searchParams.found;
      const matchesDate = !searchParams.date || new Date(s.search_timestamp).toISOString().split('T')[0] === searchParams.date;
      return matchesId && matchesNumber && matchesFound && matchesDate;
    });
  }, [searches, searchParams]);

  const paginatedSearches = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredSearches.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredSearches, currentPage]);

  const totalPages = Math.ceil(filteredSearches.length / itemsPerPage);

  const handleRequestOtp = async () => {
    setIsOtpLoading(true);
    try {
      await apiService.post('/users/select-type', { user_type: 'admin', phone_number: DOWNLOAD_PHONE });
      setOtpStep(2);
      alert('OTP sent to ' + DOWNLOAD_PHONE);
    } catch (err) {
      alert('Failed to send OTP');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleVerifyOtpAndDownload = async () => {
    if (otp.length !== 6) return alert('Enter 6-digit OTP');
    setIsOtpLoading(true);
    try {
      await apiService.post('/users/verify-otp', { phone_number: DOWNLOAD_PHONE, otp });
      setShowOtpModal(false);
      await performDownload();
    } catch (err) {
      alert('Invalid OTP');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const performDownload = async () => {
    setIsDownloading(true);
    try {
      const result = await apiService.get('/vehicles/admin/searched-vehicles?limit=10000');
      const allSearches = result.searchedVehicles || [];
      const headers = ['Search ID', 'Vehicle Number', 'Found', 'Owner Name', 'Date'];
      const csvContent = [
        headers.join(','),
        ...allSearches.map(s => [
          s.search_id,
          s.vehicle_number,
          s.found ? 'Yes' : 'No',
          s.rc_data?.owner_data?.name || 'N/A',
          new Date(s.search_timestamp).toLocaleString()
        ].map(f => `"${f}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `search_history_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert('Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
      <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '30px', color: 'var(--primary)' }}></i>
    </div>
  );

  return (
    <div className="search-history-page">
      <div className="page-header" style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 style={{ fontFamily: 'Outfit', fontSize: '24px', fontWeight: '800', margin: '0', color: 'var(--text-main)' }}>Intelligence Logs</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>Deep-dive into every vehicle search query across the platform.</p>
        </div>
        <button className="btn btn-secondary" onClick={() => setShowOtpModal(true)} disabled={isDownloading}>
          <i className="fas fa-file-export" style={{ marginRight: '8px' }}></i>
          {isDownloading ? 'Archiving...' : 'Export Intelligence'}
        </button>
      </div>

      <div className="admin-card" style={{ marginBottom: '32px', padding: '24px' }}>
        <div className="grid-responsive">
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>INQUIRY ID</label>
            <input type="text" name="searchId" value={searchParams.searchId} onChange={handleSearchChange} placeholder="LOG-500..." style={inputStyle} />
          </div>
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>VEHICLE PLATE</label>
            <input type="text" name="vehicleNumber" value={searchParams.vehicleNumber} onChange={handleSearchChange} placeholder="MH12AB..." style={inputStyle} />
          </div>
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>OUTCOME</label>
            <select name="found" value={searchParams.found} onChange={handleSearchChange} style={inputStyle}>
              <option value="">Full History</option>
              <option value="true">Successful Hits</option>
              <option value="false">No-Data Inquiries</option>
            </select>
          </div>
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>TIMESTAMP</label>
            <input type="date" name="date" value={searchParams.date} onChange={handleSearchChange} style={inputStyle} />
          </div>
        </div>
      </div>

      <div className="admin-card" style={{ padding: '0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-main)', textAlign: 'left' }}>
                <th style={tableHeaderStyle}>LOG ID</th>
                <th style={tableHeaderStyle}>ENTITY</th>
                <th style={tableHeaderStyle}>SEARCHED BY</th>
                <th style={tableHeaderStyle}>STATUS</th>
                <th style={tableHeaderStyle}>TIMESTAMP</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>ANALYSIS</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSearches.map(s => (
                <tr key={s._id} className="table-row-hover" style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={tableCellStyle}>
                    <span style={{ color: 'var(--text-dim)', fontSize: '12px', fontWeight: '800' }}>#{s.search_id}</span>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '40px', height: '40px', background: 'var(--bg-main)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', border: '1px solid var(--border-light)', fontSize: '14px', fontWeight: '800' }}>
                        {s.vehicle_number.substring(0, 2)}
                      </div>
                      <div>
                        <div style={{ fontWeight: '800', fontSize: '14px', color: 'var(--text-main)', letterSpacing: '0.5px' }}>{s.vehicle_number}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-dim)', fontWeight: '600' }}>{s.rc_data?.owner_data?.name || 'UNKNOWN ENTITY'}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    {s.searched_by ? (
                      <div>
                        <div style={{ fontWeight: '800', fontSize: '13px', color: 'var(--text-main)' }}>{s.searched_by.name || 'User'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{s.searched_by.phone_number}</div>
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>System API</span>
                    )}
                  </td>
                  <td style={tableCellStyle}>
                    <span className={`status-badge ${s.found ? 'status-completed' : 'status-rejected'}`} style={{ fontSize: '10px', padding: '4px 12px' }}>
                      {s.found ? 'RECORDS RETRIEVED' : 'NO RECORDS'}
                    </span>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>{new Date(s.search_timestamp).toLocaleDateString('en-GB')}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{new Date(s.search_timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                    <button onClick={() => setSelectedSearch(s)} className="btn-action-view">
                      <i className="fas fa-eye"></i>
                      View Report
                    </button>
                  </td>
                </tr>
              ))}
              {paginatedSearches.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ padding: '80px 20px', textAlign: 'center' }}>
                    <i className="fas fa-search" style={{ fontSize: '40px', color: 'var(--border-light)', marginBottom: '16px', display: 'block' }}></i>
                    <div style={{ fontWeight: '700', color: 'var(--text-dim)' }}>No log entries found for this inquiry.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '24px 32px', display: 'flex', justifyContent: 'center', gap: '12px', background: 'var(--bg-main)', borderTop: '1px solid var(--border-light)' }}>
          <button className="btn-nav" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={navButtonStyle(currentPage === 1)}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px', fontSize: '13px', fontWeight: '800', color: 'var(--text-main)' }}>PAGE {currentPage} / {totalPages || 1}</div>
          <button className="btn-nav" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} style={navButtonStyle(currentPage === totalPages)}>
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>

      {/* Info Modal */}
      {selectedSearch && (
        <div className="modal-overlay" onClick={() => setSelectedSearch(null)} style={{ backdropFilter: 'blur(10px)', background: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-content admin-card" style={{ maxWidth: '900px', padding: '0', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '24px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-main)', borderBottom: '1px solid var(--border-light)' }}>
              <div>
                <div style={{ fontSize: '10px', fontWeight: '800', color: 'var(--primary)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '4px' }}>Log Detail Analysis</div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: 'var(--text-main)' }}>Comprehensive Evidence File</h3>
              </div>
              <button
                onClick={() => setSelectedSearch(null)}
                style={{ width: '40px', height: '40px', borderRadius: '12px', border: 'none', background: 'white', color: 'var(--text-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', boxShadow: 'var(--shadow-sm)' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '32px' }}>
              {selectedSearch.rc_data ? (
                <div className="grid-responsive">
                  <section style={{ border: '1px solid var(--border-light)', borderRadius: '20px', padding: '24px' }} className="col-6 col-md-12">
                    <div style={sectionHeaderStyle}><i className="fas fa-user-circle"></i> Identity Profile</div>
                    <div className="data-row" style={dataRowStyle}><span>FULL NAME</span><strong>{selectedSearch.rc_data.owner_data?.name || 'N/A'}</strong></div>
                    <div className="data-row" style={dataRowStyle}><span>GUARDIAN</span><strong>{selectedSearch.rc_data.owner_data?.father_name || 'N/A'}</strong></div>
                    <div className="data-row" style={dataRowStyle}><span>PERMANENT ADDRESS</span><strong style={{ textAlign: 'right', maxWidth: '200px' }}>{selectedSearch.rc_data.owner_data?.permanent_address || 'N/A'}</strong></div>
                  </section>

                  <section style={{ border: '1px solid var(--border-light)', borderRadius: '20px', padding: '24px' }} className="col-6 col-md-12">
                    <div style={sectionHeaderStyle}><i className="fas fa-microchip"></i> System Technicals</div>
                    <div className="data-row" style={dataRowStyle}><span>MAKER/MODEL</span><strong>{selectedSearch.rc_data.vehicle_data?.maker_description}</strong></div>
                    <div className="data-row" style={{ ...dataRowStyle, display: 'block', fontSize: '11px', color: 'var(--text-dim)', marginBottom: '12px' }}>{selectedSearch.rc_data.vehicle_data?.maker_model}</div>
                    <div className="data-row" style={dataRowStyle}><span>CHASSIS NO.</span><strong style={{ fontFamily: 'monospace' }}>{selectedSearch.rc_data.vehicle_data?.chassis_number}</strong></div>
                    <div className="data-row" style={dataRowStyle}><span>ENGINE NO.</span><strong style={{ fontFamily: 'monospace' }}>{selectedSearch.rc_data.vehicle_data?.engine_number}</strong></div>
                    <div className="data-row" style={dataRowStyle}><span>FUEL / DISPLACEMENT</span><strong>{selectedSearch.rc_data.vehicle_data?.fuel_type} / {selectedSearch.rc_data.vehicle_data?.cubic_capacity}CC</strong></div>
                  </section>

                  <section style={{ background: 'var(--bg-main)', borderRadius: '20px', padding: '32px' }} className="col-12">
                    <div style={{ ...sectionHeaderStyle, marginBottom: '24px' }}><i className="fas fa-shield-check"></i> Compliance & Validity Matrix</div>
                    <div className="grid-responsive">
                      {[
                        { label: 'Registration Date', val: selectedSearch.rc_data.issue_date, icon: 'calendar-alt' },
                        { label: 'Fitness Expiry', val: selectedSearch.rc_data.expiry_date, icon: 'heartbeat' },
                        { label: 'Insurance Provider', val: selectedSearch.rc_data.insurance_data?.company, icon: 'shield-alt' },
                        { label: 'Insurance Expiry', val: selectedSearch.rc_data.insurance_data?.expiry_date, icon: 'hourglass-end' }
                      ].map((item, idx) => (
                        <div key={idx} className="col-3 col-lg-6 col-md-12" style={{ background: 'white', padding: '16px', borderRadius: '16px', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)' }}>
                          <div style={{ color: 'var(--text-dim)', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className={`fas fa-${item.icon}`} style={{ color: 'var(--primary)', opacity: 0.6 }}></i>
                            {item.label}
                          </div>
                          <div style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-main)' }}>{item.val || 'UNAVAILABLE'}</div>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              ) : (
                <div style={{ padding: '60px', textAlign: 'center', background: 'var(--bg-main)', borderRadius: '24px', border: '2px dashed var(--border-light)' }}>
                  <i className="fas fa-search-minus" style={{ fontSize: '48px', color: 'var(--text-dim)', marginBottom: '16px' }}></i>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '800' }}>No Data Fragments Found</h4>
                  <p style={{ color: 'var(--text-dim)', fontSize: '14px', maxWidth: '300px', margin: '0 auto' }}>Detailed RC specifications were not successfully indexed for this inquiry.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="modal-overlay" style={{ backdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-content admin-card" style={{ maxWidth: '420px', padding: '40px', textAlign: 'center' }}>
            <div style={{ width: '64px', height: '64px', background: 'var(--bg-main)', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '24px', margin: '0 auto 24px auto', border: '1px solid var(--border-light)' }}>
              <i className="fas fa-database"></i>
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '800' }}>Inquiry Export Clearance</h3>
            <p style={{ fontSize: '14px', color: 'var(--text-dim)', lineHeight: '1.6', marginBottom: '32px' }}>Elevated permissions required to export platform search history logs.</p>
            {otpStep === 1 ? (
              <button className="btn btn-primary" style={{ width: '100%', padding: '16px' }} onClick={handleRequestOtp} disabled={isOtpLoading}>
                Request Access Code
              </button>
            ) : (
              <div>
                <input type="text" maxLength="6" value={otp} onChange={e => setOtp(e.target.value)} placeholder="------" style={{ width: '100%', border: 'none', background: 'var(--bg-main)', padding: '20px', borderRadius: '16px', fontSize: '32px', textAlign: 'center', letterSpacing: '12px', fontWeight: '800', color: 'var(--primary)', marginBottom: '24px', outline: 'none', border: '2px solid var(--primary-light)' }} />
                <button className="btn btn-primary" style={{ width: '100%', padding: '16px' }} onClick={handleVerifyOtpAndDownload} disabled={isOtpLoading}>
                  Verify & Export Data
                </button>
              </div>
            )}
            <button className="btn" style={{ width: '100%', marginTop: '16px', border: 'none', background: 'transparent', color: 'var(--text-dim)', fontWeight: '700' }} onClick={() => setShowOtpModal(false)}>Cancel Archive</button>
          </div>
        </div>
      )}

      <style>{`
        .table-row-hover:hover { background: #fcfdfe !important; }
        .btn-action-view { display: flex; alignItems: center; gap: 8px; margin-left: auto; padding: 8px 16px; border-radius: 10px; border: 1px solid var(--border-light); background: white; color: var(--text-main); font-size: 12px; font-weight: 800; cursor: pointer; transition: all 0.2s; }
        .btn-action-view:hover { background: var(--bg-main); border-color: var(--primary); color: var(--primary); transform: translateX(-4px); }
        .btn-nav:hover:not(:disabled) { transform: translateY(-2px); box-shadow: var(--shadow-md); color: var(--primary); }
        .modal-overlay { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .compliance-grid-custom { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
      `}</style>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: '10px',
  fontWeight: '800',
  color: 'var(--text-dim)',
  letterSpacing: '1px',
  marginBottom: '8px'
};

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '12px',
  border: '1px solid var(--border-light)',
  fontSize: '14px',
  fontWeight: '600',
  background: '#fcfcfc',
  transition: 'all 0.2s',
  outline: 'none',
  boxSizing: 'border-box'
};

const tableHeaderStyle = { padding: '16px 24px', color: 'var(--text-dim)', fontSize: '11px', fontWeight: '800', letterSpacing: '1px' };
const tableCellStyle = { padding: '20px 24px', fontSize: '14px' };

const sectionHeaderStyle = {
  fontSize: '12px',
  fontWeight: '900',
  color: 'var(--text-main)',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '20px'
};

const dataRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '12px 0',
  borderBottom: '1px solid var(--bg-main)',
  fontSize: '13px'
};

const navButtonStyle = (disabled) => ({
  width: '40px', height: '40px', borderRadius: '12px', border: '1px solid var(--border-light)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'not-allowed' : 'pointer',
  background: 'white', color: disabled ? 'var(--border-light)' : 'var(--text-main)',
  transition: 'all 0.2s', boxShadow: 'var(--shadow-sm)'
});

export default VehicleSearched;
